import type { ApolloLink } from '@apollo/client';
import { createPersistedQueryLink as apolloCreatePersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { sha256 as cryptoHashSha256 } from 'crypto-hash';
import { sha256 as jsSha256 } from 'js-sha256';

/**
 * Configuration options for creating the persisted query link.
 */
export interface PersistedQueryLinkOptions {
  /**
   * Whether to enable Automatic Persisted Queries (APQ).
   * When disabled, queries will be sent with their full text as usual.
   * @default true
   */
  enabled?: boolean;

  /**
   * Whether to use GET requests for hashed queries.
   * When enabled, queries with only a hash will use GET instead of POST.
   * This improves caching and allows CDN/proxy optimization.
   * @default true
   */
  useGETForHashedQueries?: boolean;
}

/**
 * Creates an Apollo Link for Automatic Persisted Queries (APQ).
 *
 * APQ reduces bandwidth and improves caching by:
 * 1. Sending a hash of the query instead of the full query text
 * 2. Using GET requests for cacheable queries
 * 3. Automatically retrying with full query if server doesn't have the hash
 *
 * Flow:
 * - First request: Client sends hash → Server responds `PersistedQueryNotFound`
 * - Client auto-retries with full query + hash → Server stores mapping
 * - Subsequent requests: Client sends only hash (GET) → Server responds with data
 *
 * @param options - Configuration options for APQ
 * @returns Apollo Link configured for persisted queries, or null if disabled
 *
 * @example
 * ```typescript
 * const persistedQueriesLink = createPersistedQueryLink({
 *   enabled: true,
 *   useGETForHashedQueries: true
 * });
 *
 * const client = new ApolloClient({
 *   link: from([
 *     loggerLink,
 *     authLink,
 *     persistedQueriesLink,
 *     httpLink
 *   ]),
 *   cache: new InMemoryCache()
 * });
 * ```
 */
export function createPersistedQueryLink(options: PersistedQueryLinkOptions = {}): ApolloLink | null {
  const { enabled = true, useGETForHashedQueries = true } = options;

  // If APQ is disabled, return null so it can be filtered out of the link chain
  if (!enabled) {
    return null;
  }

  // Smart SHA-256 implementation with multiple fallbacks
  const sha256 = async (data: string): Promise<string> => {
    // Check if we're in a secure context (HTTPS/localhost)
    if (typeof window !== 'undefined' && window.crypto?.subtle) {
      try {
        // Primary: Use crypto-hash (best performance, uses native crypto API)
        return await cryptoHashSha256(data);
      } catch (cryptoHashError) {
        // If crypto-hash fails, try native Web Crypto API directly
        try {
          const encoder = new TextEncoder();
          const dataBuffer = encoder.encode(data);
          const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
        } catch (nativeError) {
          // If both crypto methods fail, fall back to js-sha256
          console.warn('crypto-hash and native Web Crypto API failed, falling back to js-sha256:', {
            cryptoHashError,
            nativeError,
          });
          return jsSha256(data);
        }
      }
    } else {
      // HTTP environment or crypto.subtle not available - use js-sha256
      // This enables APQ to work in development on HTTP
      return jsSha256(data);
    }
  };

  return apolloCreatePersistedQueryLink({
    // Use smart SHA-256 with fallbacks for maximum compatibility
    sha256,

    // Use GET for hashed queries to enable CDN/proxy caching
    useGETForHashedQueries,
  });
}
